import { Controller } from "@hotwired/stimulus"

// UI-17: one-question-per-screen kiosk wizard, shared by the guest book
// (VS-06) and Ijin Keluar (LV-11). Steps are `section[data-wizard-target=step]`;
// a step's `[data-wizard-focus]` input gets focus, and a `data-optional` step
// can be left blank.
export default class extends Controller {
  static targets = ["wizard", "step", "progressItem", "progressNumber", "keyboard", "summary", "thanks"]
  static values = { step: Number, thanks: Boolean }

  connect() {
    this.furthest = this.stepValue
    if (this.thanksValue) {
      this.thanksTimer = setTimeout(() => this.dismissThanks(), 5000)
    } else {
      this.show(this.stepValue)
    }
  }

  disconnect() {
    clearTimeout(this.thanksTimer)
  }

  next() {
    if (!this.currentStepFilled) return

    if (this.stepValue === this.stepTargets.length - 1) {
      this.element.querySelector("form").requestSubmit()
    } else {
      this.show(this.stepValue + 1)
    }
  }

  back() {
    this.show(this.stepValue - 1)
  }

  skip() {
    if (this.currentInput) this.currentInput.value = ""
    this.show(this.stepValue + 1)
  }

  goto(event) {
    const index = Number(event.params.step)
    if (index <= this.furthest) this.show(index)
  }

  validate() {
    this.refreshNext()
  }

  showKeyboard() {
    if (this.hasKeyboardTarget) this.keyboardTarget.hidden = false
  }

  dismissThanks() {
    clearTimeout(this.thanksTimer)
    this.thanksTarget.hidden = true
    this.wizardTarget.hidden = false
    this.show(0)
  }

  // private

  show(index) {
    const last = this.stepTargets.length - 1
    index = Math.max(0, Math.min(index, last))
    this.stepValue = index
    this.furthest = Math.max(this.furthest, index)

    this.stepTargets.forEach((step, i) => { step.hidden = i !== index })
    this.renderProgress()
    if (this.hasKeyboardTarget) this.keyboardTarget.hidden = !this.currentInput
    if (this.hasSummaryTarget) this.fillSummary()
    this.refreshNext()

    window.scrollTo(0, 0)
    const input = this.currentInput
    if (input) {
      input.focus({ preventScroll: true })
      input.setSelectionRange(input.value.length, input.value.length)
    }
  }

  get currentStep() {
    return this.stepTargets[this.stepValue]
  }

  get currentInput() {
    return this.currentStep?.querySelector("[data-wizard-focus]")
  }

  get currentStepFilled() {
    const input = this.currentInput
    return !input || "optional" in this.currentStep.dataset || input.value.trim() !== ""
  }

  refreshNext() {
    const button = this.currentStep?.querySelector('[data-wizard-target="next"]')
    if (button && this.currentInput) button.disabled = !this.currentStepFilled
  }

  renderProgress() {
    this.progressItemTargets.forEach((item, i) => {
      const number = this.progressNumberTargets[i]
      const current = i === this.stepValue
      const reachable = i <= this.furthest
      item.classList.toggle("bg-violet-100", current)
      item.classList.toggle("text-violet-800", current)
      item.classList.toggle("text-slate-400", !reachable)
      item.classList.toggle("text-slate-700", reachable && !current)
      item.disabled = !reachable
      number.classList.toggle("bg-violet-700", current)
      number.classList.toggle("text-white", current)
      number.classList.toggle("bg-violet-200", reachable && !current)
      number.classList.toggle("bg-slate-200", !reachable)
    })
  }

  // Summary rows show the current value of the input named in data-input-name
  // (for radios, the checked one's data-summary).
  fillSummary() {
    this.summaryTargets.forEach((summary) => {
      const inputs = [...this.element.querySelectorAll(`[name="${summary.dataset.inputName}"]`)]
      const input = inputs.find((element) => element.type !== "radio" || element.checked)
      summary.textContent = input?.dataset.summary || input?.value?.trim() || "—"
    })
  }
}
